import { getApiErrorMessage } from '@quoruum/frontend-common/api/errorMessage';
import { queryClient } from '@quoruum/frontend-common/api/queryClient';
import { log } from '@quoruum/frontend-common/logger';
import {
  rateScrapeMetadataActionAddProblemMutation,
  rateScrapeMetadataActionClearMutation,
  rateScrapeMetadataActionPromoteMutation,
  rateScrapeMetadataQueryOptions,
  rateScrapeMetadataQueryQueryKey,
  rateScrapeMetadataSaveMutation,
  sharedRateScrapeMetadataGetOptions,
} from '@quoruum/frontend-common/oas/gen/admin/@tanstack/react-query.gen';
import type {
  DateParamBean,
  IndicatorMetadataBean,
  RateScrapeMetadataBean,
  RateScrapeMetadataBeanWritable,
  SharedRateScrapeMetadataBean,
} from '@quoruum/frontend-common/oas/gen/admin/types.gen';
import {
  BOOKING_SITES,
  DATE_PARAM_OPTIONS,
  DATE_PARAM_TYPES,
  LAMBDA_SIZES,
  RATE_SCRAPE_METADATA_FIELDS_TO_CLEAR,
  isBookingSite,
  type BookingSite,
  type LambdaSize,
  type RateScrapeMetadataFieldToClear,
} from '@quoruum/frontend-common/oas/gen/enumValues.gen';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { ReactElement, ReactNode } from 'react';
import { useMemo, useState } from 'react';
import ReactDiffViewer from 'react-diff-viewer-continued';
import { Link } from 'react-router';
import { DeleteConfirmationModal } from '../../components/DeleteConfirmationModal';
import type {
  EditableFieldSpec,
  EditableFieldValue,
  EditableRowListColumn,
  EntityDetailRow,
  RowListEntry,
} from '../../components/EditableEntityTable';
import { EditableEntityTable } from '../../components/EditableEntityTable';
import { IssueListTable } from '../../components/IssueListTable';
import {
  BOOKING_ENGINE_TYPE_OPTIONS,
  type BookingEngineType,
} from '../../constants/bookingEngines';
import { UrlTemplateModal } from '../UrlTemplateModal';
import { formatUnknownValue, isObjectRecord } from './hotelMapShared';

type IssueField = 'HOME_PAGE' | 'URL_TEMPLATE' | 'DATE_FORMAT' | 'SELECTORS' | 'SOLD_OUT';

type RateScrapeMetadataIssuesRenderOptions = {
  onClearIssueClick: (field: Exclude<IssueField, 'HOME_PAGE'>, issue: unknown) => void;
  onOpenUrlTemplateClick: () => void;
  sourceBookingEngineLink: string | null;
  sourceBookingEngineBootstrapHotelIds: string[];
};

type EditableRateScrapeMetadataFieldKey =
  | 'sourceBookingEngine'
  | 'localHotelId'
  | 'urlTemplate'
  | 'dateFormat'
  | 'soldOut'
  | 'learnExtra'
  | 'selectorSets'
  | 'indicatorSets'
  | 'learnLambdaSize'
  | 'shopLambdaSize'
  | 'dateFormats';

const DATE_FORMATS_ROW_LIST_COLUMNS: ReadonlyArray<EditableRowListColumn> = [
  { key: 'queryParam', label: 'Query Param', type: 'text' },
  { key: 'dateFormat', label: 'Date Format', type: 'text' },
  {
    key: 'options',
    label: 'Options',
    type: 'select',
    options: DATE_PARAM_OPTIONS,
    allowEmptyOption: true,
    emptyOptionLabel: '-',
  },
  {
    key: 'type',
    label: 'Type',
    type: 'select',
    options: DATE_PARAM_TYPES,
    allowEmptyOption: true,
    emptyOptionLabel: '-',
  },
];

// Mapping from API field names (in RATE_SCRAPE_METADATA_FIELDS_TO_CLEAR) to editable field keys
const CLEAR_FIELD_API_TO_EDITABLE_KEY: Partial<
  Record<RateScrapeMetadataFieldToClear, EditableRateScrapeMetadataFieldKey>
> = {
  urlTemplate: 'urlTemplate',
  dateFormat: 'dateFormat',
  localHotelId: 'localHotelId',
  learnExtra: 'learnExtra',
  selectors: 'selectorSets',
  indicators: 'indicatorSets',
  sourceBookingEngine: 'sourceBookingEngine',
};

// Automatically derive clearable field keys from RATE_SCRAPE_METADATA_FIELDS_TO_CLEAR
const CLEARABLE_SCRAPE_METADATA_FIELD_KEYS = new Set<EditableRateScrapeMetadataFieldKey>(
  RATE_SCRAPE_METADATA_FIELDS_TO_CLEAR.map(
    (field) => CLEAR_FIELD_API_TO_EDITABLE_KEY[field]
  ).filter((key): key is EditableRateScrapeMetadataFieldKey => key !== undefined)
);

// Get the API field name for a given editable field key
function getClearFieldApiName(
  editableFieldKey: EditableRateScrapeMetadataFieldKey
): RateScrapeMetadataFieldToClear | undefined {
  for (const [apiName, editKey] of Object.entries(CLEAR_FIELD_API_TO_EDITABLE_KEY)) {
    if (editKey === editableFieldKey) {
      return apiName as RateScrapeMetadataFieldToClear;
    }
  }
  return undefined;
}

function parseJsonArrayValue<T>(value: string): Array<T> {
  const parsedValue = parseJsonValue<unknown>(value);

  if (!Array.isArray(parsedValue)) {
    throw new Error('Enter a JSON array.');
  }

  return parsedValue as Array<T>;
}

function parseJsonObjectValue(value: string): Record<string, unknown> {
  const parsedValue = parseJsonValue<unknown>(value);

  if (typeof parsedValue !== 'object' || parsedValue === null || Array.isArray(parsedValue)) {
    throw new Error('Enter a JSON object.');
  }

  return parsedValue as Record<string, unknown>;
}

function parseJsonValue<T>(value: string): T {
  const trimmedValue = value.trim();
  try {
    return JSON.parse(trimmedValue) as T;
  } catch {
    throw new Error('Enter valid JSON.');
  }
}

function validateJsonValue(value: string): string | null {
  if (!value.trim()) {
    return null;
  }

  try {
    parseJsonValue(value);
    return null;
  } catch (error) {
    return error instanceof Error ? error.message : 'Enter valid JSON.';
  }
}

const RATE_SCRAPE_METADATA_EDITABLE_FIELD_SPECS: ReadonlyMap<
  EditableRateScrapeMetadataFieldKey,
  EditableFieldSpec
> = new Map([
  [
    'sourceBookingEngine',
    {
      editType: 'select' as const,
      options: BOOKING_ENGINE_TYPE_OPTIONS,
      allowEmptyOption: true,
      emptyOptionLabel: '-',
    },
  ],
  ['localHotelId', { editType: 'text' as const }],
  ['urlTemplate', { editType: 'text' as const }],
  ['dateFormat', { editType: 'text' as const }],
  [
    'selectorSets',
    { editType: 'textarea' as const, validateValue: validateJsonValue, heightPx: 260 },
  ],
  [
    'indicatorSets',
    { editType: 'textarea' as const, validateValue: validateJsonValue, heightPx: 260 },
  ],
  ['soldOut', { editType: 'textarea' as const, validateValue: validateJsonValue, heightPx: 140 }],
  [
    'learnExtra',
    { editType: 'textarea' as const, validateValue: validateJsonValue, heightPx: 180 },
  ],
  [
    'learnLambdaSize',
    {
      editType: 'select' as const,
      options: LAMBDA_SIZES,
      allowEmptyOption: true,
      emptyOptionLabel: '-',
    },
  ],
  [
    'shopLambdaSize',
    {
      editType: 'select' as const,
      options: LAMBDA_SIZES,
      allowEmptyOption: true,
      emptyOptionLabel: '-',
    },
  ],
  [
    'dateFormats',
    {
      editType: 'row-list' as const,
      columns: DATE_FORMATS_ROW_LIST_COLUMNS,
    },
  ],
]);

const RATE_SCRAPE_METADATA_ISSUE_FIELD_BY_KEY: Record<string, Exclude<IssueField, 'HOME_PAGE'>> = {
  urlTemplateIssues: 'URL_TEMPLATE',
  soldOutIssues: 'SOLD_OUT',
  selectorsIssues: 'SELECTORS',
  dateFormatIssues: 'DATE_FORMAT',
};

const RATE_SCRAPE_METADATA_DISPLAY_KEYS: Array<keyof RateScrapeMetadataBean> = [
  'sourceBookingEngine',
  'urlTemplate',
  'dateFormat',
  'dateFormats',
  'hotelId',
  'externalId',
  'localHotelId',
  'language',
  'selectorSets',
  'indicatorSets',
  'botDetection',
  'learnExtra',
  'learnLambdaSize',
  'shopLambdaSize',
  'fieldsToClear',
  'hasSoldOutIssue',
  'urlTemplateIssues',
  'dateFormatIssues',
  'selectorsIssues',
  'soldOutIssues',
  'auditBean',
];

const SHARED_RATE_SCRAPE_METADATA_DIFF_FIELDS: ReadonlyArray<
  | 'urlTemplate'
  | 'dateFormat'
  | 'dateFormats'
  | 'selectorSets'
  | 'indicatorSets'
  | 'botDetection'
  | 'learnExtra'
  | 'shopLambdaSize'
> = [
  'urlTemplate',
  'dateFormat',
  'dateFormats',
  'selectorSets',
  'indicatorSets',
  'botDetection',
  'learnExtra',
  'shopLambdaSize',
];

function renderDateFormatsValue(entries: RowListEntry[]): ReactNode {
  if (entries.length === 0) {
    return '-';
  }

  return (
    <table className="w-full table-fixed border-collapse text-left text-xs">
      <thead>
        <tr className="border-b border-slate-200">
          <th className="px-1 py-1 font-medium text-slate-500">Query Param</th>
          <th className="px-1 py-1 font-medium text-slate-500">Date Format</th>
          <th className="px-1 py-1 font-medium text-slate-500">Options</th>
          <th className="px-1 py-1 font-medium text-slate-500">Type</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((entry, index) => (
          <tr key={index} className="border-b border-slate-100 last:border-b-0">
            <td className="break-words px-1 py-1 text-slate-800">{entry.queryParam || '-'}</td>
            <td className="break-words px-1 py-1 text-slate-800">{entry.dateFormat || '-'}</td>
            <td className="break-words px-1 py-1 text-slate-800">{entry.options || '-'}</td>
            <td className="break-words px-1 py-1 text-slate-800">{entry.type || '-'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function buildRateScrapeMetadataRows(
  metadata: RateScrapeMetadataBean,
  rateScrapeMetadataIssuesRenderOptions: RateScrapeMetadataIssuesRenderOptions
): Array<EntityDetailRow<EditableRateScrapeMetadataFieldKey>> {
  return RATE_SCRAPE_METADATA_DISPLAY_KEYS.map((key) => {
    const issueField = RATE_SCRAPE_METADATA_ISSUE_FIELD_BY_KEY[key];
    const value = metadata[key];

    if (issueField && Array.isArray(value)) {
      return {
        key,
        value: (
          <IssueListTable
            issues={value}
            onClearIssueClick={(issue) =>
              rateScrapeMetadataIssuesRenderOptions.onClearIssueClick(issueField, issue)
            }
            renderIssue={formatUnknownValue}
          />
        ),
      };
    }

    const formattedValue = formatUnknownValue(value);
    if (key === 'sourceBookingEngine') {
      const sourceBookingEngineValue = typeof value === 'string' ? value : '';
      const sourceBookingEngineLink = rateScrapeMetadataIssuesRenderOptions.sourceBookingEngineLink;
      const shouldLinkSourceBookingEngine =
        sourceBookingEngineValue !== '' && sourceBookingEngineLink !== null;
      const sourceBookingEngineBootstrapHotelIds =
        rateScrapeMetadataIssuesRenderOptions.sourceBookingEngineBootstrapHotelIds;

      return {
        key,
        value: shouldLinkSourceBookingEngine ? (
          <>
            <Link
              to={sourceBookingEngineLink}
              className="text-sky-700 underline hover:text-sky-800"
            >
              {sourceBookingEngineValue}
            </Link>
            {sourceBookingEngineBootstrapHotelIds.length > 0 ? (
              <>
                {' '}
                (bootstrap learners:{' '}
                {sourceBookingEngineBootstrapHotelIds.map((hotelId, index) => (
                  <span key={hotelId}>
                    {index > 0 ? ', ' : ''}
                    <Link
                      to={`/tools/hotel-explorer?selectedId=${encodeURIComponent(hotelId)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sky-700 underline hover:text-sky-800"
                    >
                      {hotelId}
                    </Link>
                  </span>
                ))}
                )
              </>
            ) : null}
          </>
        ) : (
          formattedValue
        ),
        editableFieldKey: 'sourceBookingEngine',
        editableFieldValue: sourceBookingEngineValue,
      };
    }

    if (key === 'localHotelId') {
      return {
        key,
        value: formattedValue,
        editableFieldKey: 'localHotelId',
        editableFieldValue: typeof value === 'string' ? value : '',
      };
    }

    if (key === 'dateFormat') {
      return {
        key,
        value: formattedValue,
        editableFieldKey: 'dateFormat',
        editableFieldValue: typeof value === 'string' ? value : '',
      };
    }

    if (key === 'urlTemplate') {
      const hasUrlTemplateValue = typeof formattedValue === 'string' && formattedValue !== '-';
      return {
        key,
        value: hasUrlTemplateValue ? (
          <a
            href="#"
            onClick={(event) => {
              event.preventDefault();
              rateScrapeMetadataIssuesRenderOptions.onOpenUrlTemplateClick();
            }}
            className="break-all text-left text-sky-700 underline"
          >
            {formattedValue}
          </a>
        ) : (
          formattedValue
        ),
        editableFieldKey: 'urlTemplate',
        editableFieldValue: typeof value === 'string' ? value : '',
      };
    }

    if (key === 'learnExtra') {
      return {
        key,
        value: formattedValue,
        editableFieldKey: 'learnExtra',
        editableFieldValue: value === undefined ? '' : JSON.stringify(value, null, 2),
      };
    }

    if (key === 'learnLambdaSize') {
      return {
        key,
        value: formattedValue,
        editableFieldKey: 'learnLambdaSize',
        editableFieldValue: typeof value === 'string' ? value : '',
      };
    }

    if (key === 'shopLambdaSize') {
      return {
        key,
        value: formattedValue,
        editableFieldKey: 'shopLambdaSize',
        editableFieldValue: typeof value === 'string' ? value : '',
      };
    }

    if (key === 'selectorSets') {
      return {
        key,
        value: formattedValue,
        editableFieldKey: 'selectorSets',
        editableFieldValue: value === undefined ? '' : JSON.stringify(value, null, 2),
      };
    }

    if (key === 'indicatorSets') {
      return {
        key,
        value: formattedValue,
        editableFieldKey: 'indicatorSets',
        editableFieldValue: value === undefined ? '' : JSON.stringify(value, null, 2),
      };
    }

    if (key === 'dateFormats' && Array.isArray(value)) {
      const dateFormatsRowListValue: RowListEntry[] = value.map((entry) => {
        const record: Record<string, unknown> = isObjectRecord(entry) ? entry : {};
        const options = record.options;

        return {
          queryParam: typeof record.queryParam === 'string' ? record.queryParam : '',
          dateFormat: typeof record.dateFormat === 'string' ? record.dateFormat : '',
          options: Array.isArray(options) && typeof options[0] === 'string' ? options[0] : '',
          type: typeof record.type === 'string' ? record.type : '',
        };
      });

      return {
        key,
        value: renderDateFormatsValue(dateFormatsRowListValue),
        editableFieldKey: 'dateFormats',
        editableFieldRowListValue: dateFormatsRowListValue,
      };
    }

    return {
      key,
      value: formattedValue,
    };
  });
}

function buildComparableSharedMetadataShape(
  metadata?: Partial<RateScrapeMetadataBean> | Partial<SharedRateScrapeMetadataBean>
): Record<string, unknown> {
  const comparableMetadata: Record<string, unknown> = {};

  for (const key of SHARED_RATE_SCRAPE_METADATA_DIFF_FIELDS) {
    const value = metadata?.[key];
    comparableMetadata[key] = value === undefined ? null : value;
  }

  return comparableMetadata;
}

function extractBootstrapHotelIds(value: SharedRateScrapeMetadataBean | undefined): string[] {
  return value?.bootstrapHotelIds ?? [];
}

type SourceBookingEngine = NonNullable<RateScrapeMetadataBean['sourceBookingEngine']> | '';

export function useSelectedRateScrapeMetadata(
  selectedHotelId: string | undefined,
  scrapeMetadataSite: BookingSite
) {
  const {
    data: selectedRateScrapeMetadataQueryResponse,
    error: selectedRateScrapeMetadataError,
    isError: hasSelectedRateScrapeMetadataError,
    isLoading: isSelectedRateScrapeMetadataLoading,
  } = useQuery({
    ...(selectedHotelId
      ? rateScrapeMetadataQueryOptions({
          query: {
            externalIds: [`${scrapeMetadataSite}:${selectedHotelId}`],
          },
        })
      : rateScrapeMetadataQueryOptions()),
    enabled: Boolean(selectedHotelId),
    staleTime: 60 * 1000,
    placeholderData: (previousData) => previousData,
  });
  const selectedRateScrapeMetadata =
    selectedRateScrapeMetadataQueryResponse?.results?.find(
      (metadata) => metadata.hotelId === selectedHotelId
    ) ?? selectedRateScrapeMetadataQueryResponse?.results?.[0];
  const selectedSourceBookingEngine: SourceBookingEngine =
    typeof selectedRateScrapeMetadata?.sourceBookingEngine === 'string'
      ? selectedRateScrapeMetadata.sourceBookingEngine
      : '';

  return {
    selectedRateScrapeMetadataError,
    hasSelectedRateScrapeMetadataError,
    isSelectedRateScrapeMetadataLoading,
    selectedRateScrapeMetadata,
    selectedSourceBookingEngine,
  };
}

function useSharedRateScrapeMetadata(
  selectedSourceBookingEngine: SourceBookingEngine,
  isEnabled: boolean
) {
  const {
    data: sharedRateScrapeMetadata,
    isLoading: isSharedRateScrapeMetadataLoading,
    isError: hasSharedRateScrapeMetadataError,
    error: sharedRateScrapeMetadataError,
  } = useQuery({
    ...(selectedSourceBookingEngine
      ? sharedRateScrapeMetadataGetOptions({
          path: {
            bookingEngine: selectedSourceBookingEngine,
          },
        })
      : sharedRateScrapeMetadataGetOptions({
          path: {
            bookingEngine: 'MARRIOTT',
          },
        })),
    enabled: Boolean(selectedSourceBookingEngine) && isEnabled,
    staleTime: 30 * 1000,
  });
  const sourceBookingEngineLink = useMemo(() => {
    if (!selectedSourceBookingEngine || !sharedRateScrapeMetadata) {
      return null;
    }

    return `/tools/booking-engines?bookingEngineType=${encodeURIComponent(selectedSourceBookingEngine)}`;
  }, [selectedSourceBookingEngine, sharedRateScrapeMetadata]);
  const sourceBookingEngineBootstrapHotelIds = useMemo(() => {
    if (!sourceBookingEngineLink) {
      return [];
    }

    return extractBootstrapHotelIds(sharedRateScrapeMetadata);
  }, [sharedRateScrapeMetadata, sourceBookingEngineLink]);

  return {
    sharedRateScrapeMetadata,
    isSharedRateScrapeMetadataLoading,
    hasSharedRateScrapeMetadataError,
    sharedRateScrapeMetadataError,
    sourceBookingEngineLink,
    sourceBookingEngineBootstrapHotelIds,
  };
}

// Reads the query cache only; shared metadata is fetched while the Scrape Metadata tab is open.
export function useIsBootstrapLearner(
  selectedHotelId: string | undefined,
  scrapeMetadataSite: BookingSite
): boolean {
  const { selectedSourceBookingEngine } = useSelectedRateScrapeMetadata(
    selectedHotelId,
    scrapeMetadataSite
  );
  const { sourceBookingEngineBootstrapHotelIds } = useSharedRateScrapeMetadata(
    selectedSourceBookingEngine,
    false
  );

  return (
    selectedHotelId !== undefined && sourceBookingEngineBootstrapHotelIds.includes(selectedHotelId)
  );
}

type ScrapeMetadataSectionProps = {
  selectedHotelId: string | undefined;
  scrapeMetadataSite: BookingSite;
  setScrapeMetadataSite: (site: BookingSite) => void;
};

export function ScrapeMetadataSection({
  selectedHotelId,
  scrapeMetadataSite,
  setScrapeMetadataSite,
}: ScrapeMetadataSectionProps): ReactElement {
  const [isUrlTemplateModalOpen, setIsUrlTemplateModalOpen] = useState(false);
  const [isClearMetadataModalOpen, setIsClearMetadataModalOpen] = useState(false);
  const [clearMetadataConfirmation, setClearMetadataConfirmation] = useState('');
  const [isPromoteMetadataModalOpen, setIsPromoteMetadataModalOpen] = useState(false);
  const [promoteMetadataConfirmation, setPromoteMetadataConfirmation] = useState('');
  const [promoteMetadataError, setPromoteMetadataError] = useState<string | null>(null);
  const [isClearFieldModalOpen, setIsClearFieldModalOpen] = useState(false);
  const [clearingFieldKey, setClearingFieldKey] =
    useState<EditableRateScrapeMetadataFieldKey | null>(null);
  const [clearFieldConfirmation, setClearFieldConfirmation] = useState('');
  const [clearFieldError, setClearFieldError] = useState<string | null>(null);
  const [isClearIssueModalOpen, setIsClearIssueModalOpen] = useState(false);
  const [clearIssueConfirmation, setClearIssueConfirmation] = useState('');
  const [clearIssueTitle, setClearIssueTitle] = useState('');
  const [selectedIssueBody, setSelectedIssueBody] = useState<Record<string, unknown> | null>(null);
  const [selectedIssueField, setSelectedIssueField] = useState<Exclude<
    IssueField,
    'HOME_PAGE'
  > | null>(null);
  const {
    selectedRateScrapeMetadataError,
    hasSelectedRateScrapeMetadataError,
    isSelectedRateScrapeMetadataLoading,
    selectedRateScrapeMetadata,
    selectedSourceBookingEngine,
  } = useSelectedRateScrapeMetadata(selectedHotelId, scrapeMetadataSite);
  const {
    sharedRateScrapeMetadata,
    isSharedRateScrapeMetadataLoading,
    hasSharedRateScrapeMetadataError,
    sharedRateScrapeMetadataError,
    sourceBookingEngineLink,
    sourceBookingEngineBootstrapHotelIds,
  } = useSharedRateScrapeMetadata(selectedSourceBookingEngine, true);
  const promoteDiffOldValue = useMemo(() => {
    const localComparableMetadata = buildComparableSharedMetadataShape(selectedRateScrapeMetadata);
    return JSON.stringify(localComparableMetadata, null, 2);
  }, [selectedRateScrapeMetadata]);
  const promoteDiffNewValue = useMemo(() => {
    const sharedComparableMetadata = buildComparableSharedMetadataShape(sharedRateScrapeMetadata);
    return JSON.stringify(sharedComparableMetadata, null, 2);
  }, [sharedRateScrapeMetadata]);
  const clearMetadataMutation = useMutation(rateScrapeMetadataActionClearMutation());
  const saveRateScrapeMetadataMutation = useMutation(rateScrapeMetadataSaveMutation());
  const clearRateScrapeMetadataIssueMutation = useMutation(
    rateScrapeMetadataActionAddProblemMutation()
  );
  const promoteMetadataToSharedMutation = useMutation(rateScrapeMetadataActionPromoteMutation());
  function handleOpenClearFieldModal(fieldKey: EditableRateScrapeMetadataFieldKey): void {
    if (!getClearFieldApiName(fieldKey)) {
      return;
    }
    setClearingFieldKey(fieldKey);
    setClearFieldConfirmation('');
    setClearFieldError(null);
    setIsClearFieldModalOpen(true);
  }
  async function handleClearField(): Promise<void> {
    if (!selectedHotelId || !clearingFieldKey) {
      return;
    }

    const externalId =
      selectedRateScrapeMetadata?.externalId?.id && selectedRateScrapeMetadata.externalId.site
        ? selectedRateScrapeMetadata.externalId
        : { site: scrapeMetadataSite, id: selectedHotelId };

    try {
      const apiFieldName = getClearFieldApiName(clearingFieldKey);
      if (!apiFieldName) {
        setClearFieldError('Unable to clear field.');
        return;
      }
      await saveRateScrapeMetadataMutation.mutateAsync({
        path: {
          externalId: `${externalId.site}:${externalId.id}`,
        },
        body: {
          hotelId: selectedHotelId,
          externalId,
          fieldsToClear: [apiFieldName],
        },
      });
      setClearFieldConfirmation('');
      setClearFieldError(null);
      setIsClearFieldModalOpen(false);
      await queryClient.invalidateQueries({
        queryKey: rateScrapeMetadataQueryQueryKey(),
      });
    } catch (error) {
      let message = 'Unable to clear field.';
      if (
        typeof error === 'object' &&
        error !== null &&
        'detail' in error &&
        typeof (error as Record<string, unknown>).detail === 'string'
      ) {
        message = `Unable to clear field. ${(error as Record<string, string>).detail}`;
      }
      setClearFieldError(message);
    }
  }
  async function handleSaveRateScrapeMetadataField(
    fieldKey: EditableRateScrapeMetadataFieldKey,
    value: EditableFieldValue
  ): Promise<void> {
    if (!selectedHotelId) {
      return;
    }

    if (fieldKey === 'dateFormats') {
      if (!Array.isArray(value)) {
        throw new Error('Invalid value for selected field.');
      }

      const dateFormats = (value as RowListEntry[])
        .map((entry) => {
          const queryParam = entry.queryParam?.trim() ?? '';
          const dateFormat = entry.dateFormat?.trim() ?? '';
          const optionsValue = entry.options?.trim() ?? '';
          const typeValue = entry.type?.trim() ?? '';

          return {
            queryParam: queryParam || undefined,
            dateFormat: dateFormat || undefined,
            options: optionsValue ? [optionsValue as 'ZERO_BASED_MONTH'] : undefined,
            type: typeValue as DateParamBean['type'],
          };
        })
        .filter((entry) => entry.queryParam || entry.dateFormat);

      const externalId =
        selectedRateScrapeMetadata?.externalId?.id && selectedRateScrapeMetadata.externalId.site
          ? selectedRateScrapeMetadata.externalId
          : { site: scrapeMetadataSite, id: selectedHotelId };

      await saveRateScrapeMetadataMutation.mutateAsync({
        path: {
          externalId: `${externalId.site}:${externalId.id}`,
        },
        body: {
          hotelId: selectedHotelId,
          externalId,
          dateFormats,
        },
      });

      await queryClient.invalidateQueries({
        queryKey: rateScrapeMetadataQueryQueryKey(),
      });
      return;
    }

    if (typeof value !== 'string') {
      throw new Error('Invalid value for selected field.');
    }

    const externalId =
      selectedRateScrapeMetadata?.externalId?.id && selectedRateScrapeMetadata.externalId.site
        ? selectedRateScrapeMetadata.externalId
        : { site: scrapeMetadataSite, id: selectedHotelId };
    const soldOutMetadata =
      fieldKey === 'soldOut'
        ? value.trim()
          ? parseJsonValue<IndicatorMetadataBean>(value)
          : null
        : undefined;
    const learnExtraMetadata =
      fieldKey === 'learnExtra' ? (value.trim() ? parseJsonObjectValue(value) : null) : undefined;
    const selectorSetsMetadata =
      fieldKey === 'selectorSets' ? (value.trim() ? parseJsonArrayValue(value) : []) : undefined;
    const indicatorSetsMetadata =
      fieldKey === 'indicatorSets' ? (value.trim() ? parseJsonArrayValue(value) : []) : undefined;
    const learnLambdaSizeMetadata =
      fieldKey === 'learnLambdaSize'
        ? value
          ? (value as LambdaSize)
          : (null as unknown as LambdaSize)
        : undefined;
    const shopLambdaSizeMetadata =
      fieldKey === 'shopLambdaSize'
        ? value
          ? (value as LambdaSize)
          : (null as unknown as LambdaSize)
        : undefined;

    await saveRateScrapeMetadataMutation.mutateAsync({
      path: {
        externalId: `${externalId.site}:${externalId.id}`,
      },
      body: {
        hotelId: selectedHotelId,
        externalId,
        sourceBookingEngine:
          fieldKey === 'sourceBookingEngine'
            ? value
              ? (value as BookingEngineType)
              : null
            : undefined,
        localHotelId: fieldKey === 'localHotelId' ? value : undefined,
        urlTemplate: fieldKey === 'urlTemplate' ? value : undefined,
        dateFormat: fieldKey === 'dateFormat' ? value : undefined,
        soldOut: soldOutMetadata,
        learnExtra: learnExtraMetadata,
        learnLambdaSize: learnLambdaSizeMetadata,
        shopLambdaSize: shopLambdaSizeMetadata,
        selectorSets: selectorSetsMetadata,
        indicatorSets: indicatorSetsMetadata,
      } as Pick<
        RateScrapeMetadataBeanWritable,
        | 'hotelId'
        | 'externalId'
        | 'sourceBookingEngine'
        | 'localHotelId'
        | 'urlTemplate'
        | 'dateFormat'
        | 'learnExtra'
        | 'learnLambdaSize'
        | 'shopLambdaSize'
        | 'selectorSets'
        | 'indicatorSets'
      >,
    });

    await queryClient.invalidateQueries({
      queryKey: rateScrapeMetadataQueryQueryKey(),
    });
  }
  function handleOpenUrlTemplateModal(): void {
    if (typeof selectedRateScrapeMetadata?.urlTemplate !== 'string') {
      return;
    }
    setIsUrlTemplateModalOpen(true);
  }
  async function handleCreateRateScrapeMetadata(): Promise<void> {
    if (!selectedHotelId) {
      return;
    }

    await saveRateScrapeMetadataMutation.mutateAsync({
      path: {
        externalId: `${scrapeMetadataSite}:${selectedHotelId}`,
      },
      body: {
        hotelId: selectedHotelId,
        externalId: { site: scrapeMetadataSite, id: selectedHotelId },
      },
    });

    await queryClient.invalidateQueries({
      queryKey: rateScrapeMetadataQueryQueryKey(),
    });
  }
  async function handleClearMetadata(): Promise<void> {
    if (!selectedHotelId) {
      return;
    }

    try {
      await clearMetadataMutation.mutateAsync({
        path: {
          externalId: `${scrapeMetadataSite}:${selectedHotelId}`,
        },
      });
      setClearMetadataConfirmation('');
      setIsClearMetadataModalOpen(false);
      // Refresh the page to pick up changes
      window.location.reload();
    } catch (error) {
      log.error('Failed to clear metadata:', error);
    }
  }
  async function handlePromoteMetadataToShared(): Promise<void> {
    if (!selectedHotelId) {
      return;
    }

    try {
      await promoteMetadataToSharedMutation.mutateAsync({
        path: {
          externalId: `${scrapeMetadataSite}:${selectedHotelId}`,
        },
      });

      setPromoteMetadataConfirmation('');
      setPromoteMetadataError(null);
      setIsPromoteMetadataModalOpen(false);
    } catch (error) {
      setPromoteMetadataError(getApiErrorMessage(error, 'Failed to promote metadata to shared.'));
      log.error('Failed to promote metadata to shared:', error);
    }
  }
  function handleOpenClearIssueModal(
    issue: unknown,
    field: Exclude<IssueField, 'HOME_PAGE'>,
    title: string
  ): void {
    const issueBody = isObjectRecord(issue) ? issue : { value: issue };

    setSelectedIssueBody(issueBody);
    setSelectedIssueField(field);
    setClearIssueTitle(title);
    setClearIssueConfirmation('');
    setIsClearIssueModalOpen(true);
  }
  function handleOpenClearRateScrapeMetadataIssueModal(
    field: Exclude<IssueField, 'HOME_PAGE'>,
    issue: unknown
  ): void {
    const issueLabelByField: Record<Exclude<IssueField, 'HOME_PAGE'>, string> = {
      URL_TEMPLATE: 'URL Template',
      SOLD_OUT: 'Sold Out',
      SELECTORS: 'Selectors',
      DATE_FORMAT: 'Date Format',
    };

    handleOpenClearIssueModal(issue, field, `Clear ${issueLabelByField[field]} Issue`);
  }
  async function handleClearIssue(): Promise<void> {
    if (!selectedHotelId || !selectedIssueBody || !selectedIssueField) {
      return;
    }

    try {
      const requestBody = {
        ...selectedIssueBody,
        externalId: `${scrapeMetadataSite}:${selectedHotelId}`,
        field: selectedIssueField,
        deleted: true,
      };

      await clearRateScrapeMetadataIssueMutation.mutateAsync({
        path: {
          externalId: `${scrapeMetadataSite}:${selectedHotelId}`,
        },
        body: requestBody,
      });

      setSelectedIssueBody(null);
      setSelectedIssueField(null);
      setClearIssueTitle('');
      setClearIssueConfirmation('');
      setIsClearIssueModalOpen(false);
      await queryClient.invalidateQueries({
        queryKey: rateScrapeMetadataQueryQueryKey(),
      });
    } catch (error) {
      log.error('Failed to clear issue:', error);
    }
  }

  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto rounded-md border border-slate-200">
        {selectedHotelId ? (
          <div className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs">
            <div className="flex items-center gap-3">
              <Link
                to={`/tools/changelog?entityType=RateScrapeMetadata&entityId=${encodeURIComponent(selectedHotelId)}&site=${encodeURIComponent(scrapeMetadataSite)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-sky-700 underline"
              >
                Changelog
              </Link>
              <button
                type="button"
                onClick={() => {
                  setClearMetadataConfirmation('');
                  setIsClearMetadataModalOpen(true);
                }}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 hover:border-slate-400 hover:shadow-md"
              >
                Clear Metadata
              </button>
              {selectedSourceBookingEngine ? (
                <button
                  onClick={() => {
                    setPromoteMetadataConfirmation('');
                    setPromoteMetadataError(null);
                    setIsPromoteMetadataModalOpen(true);
                  }}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 hover:border-slate-400 hover:shadow-md"
                >
                  Promote to Shared
                </button>
              ) : null}
              <select
                value={scrapeMetadataSite}
                onChange={(event) => {
                  const nextSite = event.target.value;
                  if (isBookingSite(nextSite)) {
                    setScrapeMetadataSite(nextSite);
                  }
                }}
                className="ml-auto rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs font-medium text-slate-700 shadow-sm"
              >
                {BOOKING_SITES.map((site) => (
                  <option key={site} value={site}>
                    {site}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : null}
        {isSelectedRateScrapeMetadataLoading ? (
          <p className="px-3 py-2 text-xs text-slate-600">Loading scrape metadata...</p>
        ) : null}
        {hasSelectedRateScrapeMetadataError ? (
          <p className="px-3 py-2 text-xs text-rose-700">
            {getApiErrorMessage(selectedRateScrapeMetadataError, 'Unable to load scrape metadata.')}
          </p>
        ) : null}
        {!isSelectedRateScrapeMetadataLoading &&
        !hasSelectedRateScrapeMetadataError &&
        !selectedRateScrapeMetadata ? (
          <div className="flex items-center gap-3 px-3 py-2">
            <p className="text-xs text-slate-600">No scrape metadata found.</p>
            <button
              type="button"
              onClick={() => {
                void handleCreateRateScrapeMetadata();
              }}
              disabled={saveRateScrapeMetadataMutation.isPending}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 hover:border-slate-400 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saveRateScrapeMetadataMutation.isPending ? 'Creating...' : 'Create New'}
            </button>
          </div>
        ) : null}
        {selectedRateScrapeMetadata ? (
          <EditableEntityTable
            rows={buildRateScrapeMetadataRows(selectedRateScrapeMetadata, {
              onClearIssueClick: handleOpenClearRateScrapeMetadataIssueModal,
              onOpenUrlTemplateClick: handleOpenUrlTemplateModal,
              sourceBookingEngineLink,
              sourceBookingEngineBootstrapHotelIds,
            })}
            fieldDefinitions={RATE_SCRAPE_METADATA_EDITABLE_FIELD_SPECS}
            onSave={handleSaveRateScrapeMetadataField}
            clearableFieldKeys={CLEARABLE_SCRAPE_METADATA_FIELD_KEYS}
            onClear={handleOpenClearFieldModal}
          />
        ) : null}
      </div>
      <UrlTemplateModal
        isOpen={isUrlTemplateModalOpen}
        onClose={() => setIsUrlTemplateModalOpen(false)}
        urlTemplate={selectedRateScrapeMetadata?.urlTemplate}
        dateFormatToken={selectedRateScrapeMetadata?.dateFormat}
        dateFormats={selectedRateScrapeMetadata?.dateFormats}
        localHotelId={selectedRateScrapeMetadata?.localHotelId}
      />
      <DeleteConfirmationModal
        isOpen={isClearMetadataModalOpen}
        onClose={() => {
          setIsClearMetadataModalOpen(false);
          setClearMetadataConfirmation('');
        }}
        ariaLabel="Clear metadata confirmation"
        title="Clear Rate Scrape Metadata"
        message="Are you sure you want to delete ALL learned fields on this RateScrapeMetadata?"
        confirmationValue={clearMetadataConfirmation}
        onConfirmationChange={setClearMetadataConfirmation}
        onSubmit={() => {
          void handleClearMetadata();
        }}
        isSubmitting={clearMetadataMutation.isPending}
        isSubmitDisabled={clearMetadataConfirmation !== 'delete' || clearMetadataMutation.isPending}
        submittingLabel="Clearing..."
      />
      <DeleteConfirmationModal
        isOpen={isClearFieldModalOpen}
        onClose={() => {
          if (saveRateScrapeMetadataMutation.isPending) return;
          setIsClearFieldModalOpen(false);
          setClearingFieldKey(null);
          setClearFieldConfirmation('');
          setClearFieldError(null);
        }}
        ariaLabel="Clear field confirmation"
        title={`Clear ${clearingFieldKey ?? 'Field'}`}
        message={`Are you sure you want to clear ${clearingFieldKey ?? 'this field'}? This will remove the current value.`}
        confirmationValue={clearFieldConfirmation}
        onConfirmationChange={setClearFieldConfirmation}
        onSubmit={() => {
          void handleClearField();
        }}
        isSubmitting={saveRateScrapeMetadataMutation.isPending}
        isSubmitDisabled={
          clearFieldConfirmation !== 'delete' || saveRateScrapeMetadataMutation.isPending
        }
        submittingLabel="Clearing..."
        errorMessage={clearFieldError}
      />
      <DeleteConfirmationModal
        isOpen={isClearIssueModalOpen}
        onClose={() => {
          setIsClearIssueModalOpen(false);
          setClearIssueConfirmation('');
          setSelectedIssueBody(null);
          setSelectedIssueField(null);
          setClearIssueTitle('');
        }}
        ariaLabel="Clear issue confirmation"
        title={clearIssueTitle || 'Clear Issue'}
        message="Are you sure?"
        confirmationValue={clearIssueConfirmation}
        onConfirmationChange={setClearIssueConfirmation}
        onSubmit={() => {
          void handleClearIssue();
        }}
        isSubmitting={clearRateScrapeMetadataIssueMutation.isPending}
        isSubmitDisabled={
          clearIssueConfirmation !== 'delete' ||
          !selectedIssueBody ||
          !selectedIssueField ||
          clearRateScrapeMetadataIssueMutation.isPending
        }
      />
      <DeleteConfirmationModal
        isOpen={isPromoteMetadataModalOpen}
        onClose={() => {
          setIsPromoteMetadataModalOpen(false);
          setPromoteMetadataConfirmation('');
          setPromoteMetadataError(null);
        }}
        ariaLabel="Promote metadata to shared confirmation"
        title="Promote Rate Scrape Metadata to Shared"
        message={`Are you sure want to promote this Rate Scrape Metadata to be used by all hotels in this company using this booking engine (${selectedSourceBookingEngine})?`}
        confirmationPromptText={`Type "${selectedSourceBookingEngine}" to confirm:`}
        confirmationPlaceholder={selectedSourceBookingEngine}
        confirmationValue={promoteMetadataConfirmation}
        onConfirmationChange={(value) => {
          setPromoteMetadataConfirmation(value);
          if (promoteMetadataError) {
            setPromoteMetadataError(null);
          }
        }}
        onSubmit={() => {
          void handlePromoteMetadataToShared();
        }}
        isSubmitting={promoteMetadataToSharedMutation.isPending}
        errorMessage={promoteMetadataError}
        isSubmitDisabled={
          promoteMetadataConfirmation !== selectedSourceBookingEngine ||
          !selectedSourceBookingEngine ||
          promoteMetadataToSharedMutation.isPending
        }
        submitLabel="Yes"
        submittingLabel="Promoting..."
        panelClassName="w-full max-w-5xl rounded-lg border border-slate-200 bg-white p-4 shadow-xl"
      >
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
          <p className="mb-2 text-xs text-slate-600">
            Diffing local scrape metadata vs shared scrape metadata for booking engine{' '}
            <span className="font-semibold text-slate-800">
              {selectedSourceBookingEngine || '-'}
            </span>
            .
          </p>
          {isSharedRateScrapeMetadataLoading ? (
            <p className="text-xs text-slate-600">Loading shared metadata diff...</p>
          ) : null}
          {hasSharedRateScrapeMetadataError ? (
            <p className="text-xs text-rose-700">
              {getApiErrorMessage(
                sharedRateScrapeMetadataError,
                'Unable to load shared scrape metadata.'
              )}
            </p>
          ) : null}
          {!isSharedRateScrapeMetadataLoading && !hasSharedRateScrapeMetadataError ? (
            <div className="max-h-[60vh] overflow-auto rounded border border-slate-200 bg-white">
              <ReactDiffViewer
                oldValue={promoteDiffNewValue}
                newValue={promoteDiffOldValue}
                leftTitle="Current Shared"
                rightTitle="New Shared"
                splitView
                showDiffOnly={false}
                useDarkTheme={false}
              />
            </div>
          ) : null}
        </div>
      </DeleteConfirmationModal>
    </>
  );
}
